import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  getPenaltiesReports,
  clearError,
  clearReports,
} from "../../../../redux/Slices/authSlice";
import { FaCalendarAlt, FaFileExcel, FaPrint, FaEye } from "react-icons/fa";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useTitle } from "../../../../context/TitleContext";
import PdfPreviewModal from "../../Accounting/Reports/components/PdfPreviewModal";
import SortOrderSelect from "../../../../shared/components/SortOrderSelect";

const PenaltiesReport = () => {
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const { penaltiesReports, isLoading, error } = useSelector(
    state => state.auth,
  );
  const { t, i18n } = useTranslation("global");
  const isRTL = i18n.language === "ar";
  const textAlign = isRTL ? "right" : "left";
  const dir = isRTL ? "rtl" : "ltr";
  const locale = "en-US";
  const [reportDate, setReportDate] = useState("");
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [perPageInput, setPerPageInput] = useState(10);
  const [allReports, setAllReports] = useState([]);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [isFetchingReport, setIsFetchingReport] = useState(false);
  const [pdfChunks, setPdfChunks] = useState([]);
  const lastErrorRef = useRef({ message: null, time: 0 });
  const publicUrl = process.env.PUBLIC_URL || "";
  const headerLogo = `${publicUrl}/ACE.png`;

  // Search & Filter State
  const [searchTerm, setSearchTerm] = useState("");
  const [deptFilter, setDeptFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortField, setSortField] = useState("employee_name");
  const [sortOrder, setSortOrder] = useState("asc");

  useEffect(() => {
    setTitle("employeePenaltiesNamespace.title");
  }, [setTitle]);

  useEffect(() => {
    return () => {
      setReportDate("");
      setAllReports([]);
      setHasGenerated(false);
      dispatch(clearReports());
    };
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: isRTL });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [error, dispatch, isRTL]);

  useEffect(() => {
    if (!hasGenerated || isFetchingReport) return;
    const reportsData = penaltiesReports?.data || penaltiesReports || [];
    if (Array.isArray(reportsData) && reportsData.length > 0) {
      setAllReports(reportsData);
      setCurrentPage(1);
    } else {
      setAllReports([]);
    }
  }, [penaltiesReports, hasGenerated, isFetchingReport]);

  const handleGenerateReport = async () => {
    if (!reportDate) {
      toast.error(
        t("reports.workingHours.selectMonth", {
          defaultValue: "Please select a month",
        }),
        { rtl: isRTL },
      );
      return;
    }
    const [year, month] = reportDate.split("-");
    setAllReports([]);
    setHasGenerated(true);
    setIsFetchingReport(true);
    dispatch(clearReports());
    try {
      const result = await dispatch(
        getPenaltiesReports({ year, month }),
      ).unwrap();
      const reportsData = result?.data || result || [];
      setAllReports(Array.isArray(reportsData) ? reportsData : []);
      setCurrentPage(1);
    } catch (err) {
      setAllReports([]);
    } finally {
      setIsFetchingReport(false);
    }
  };

  useEffect(() => {
    setAllReports([]);
    setHasGenerated(false);
    dispatch(clearReports());
  }, [reportDate, dispatch]);

  const handleApplyPerPage = () => {
    const validValue = Math.max(
      1,
      Math.min(perPageInput, filteredAndSortedReports?.length || 1000),
    );
    setPerPage(validValue);
    setCurrentPage(1);
  };

  // Unique departments
  const uniqueDepartments = React.useMemo(() => {
    const list = allReports
      .map(
        item =>
          item.department ||
          (item.employee?.department
            ? (isRTL
                ? item.employee.department.name_ar
                : item.employee.department.name_en) ||
              item.employee.department.name
            : ""),
      )
      .filter(Boolean);
    return Array.from(new Set(list));
  }, [allReports, isRTL]);

  // Client-side filtering and sorting
  const filteredAndSortedReports = React.useMemo(() => {
    let result = [...allReports];

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      result = result.filter(item => {
        const empName =
          item.employee_name ||
          (item.employee
            ? (isRTL ? item.employee.name_ar : item.employee.name_en) ||
              item.employee.name
            : "");
        return empName.toLowerCase().includes(term);
      });
    }

    if (deptFilter !== "all") {
      result = result.filter(item => {
        const dept =
          item.department ||
          (item.employee?.department
            ? (isRTL
                ? item.employee.department.name_ar
                : item.employee.department.name_en) ||
              item.employee.department.name
            : "");
        return dept === deptFilter;
      });
    }

    if (statusFilter !== "all") {
      result = result.filter(item => (item.status || "") === statusFilter);
    }

    result.sort((a, b) => {
      let aVal = "";
      let bVal = "";

      if (sortField === "employee_name") {
        aVal =
          a.employee_name ||
          (a.employee
            ? (isRTL ? a.employee.name_ar : a.employee.name_en) ||
              a.employee.name
            : "");
        bVal =
          b.employee_name ||
          (b.employee
            ? (isRTL ? b.employee.name_ar : b.employee.name_en) ||
              b.employee.name
            : "");
      } else if (sortField === "department") {
        aVal =
          a.department ||
          (a.employee?.department
            ? (isRTL
                ? a.employee.department.name_ar
                : a.employee.department.name_en) || a.employee.department.name
            : "");
        bVal =
          b.department ||
          (b.employee?.department
            ? (isRTL
                ? b.employee.department.name_ar
                : b.employee.department.name_en) || b.employee.department.name
            : "");
      } else if (sortField === "amount") {
        return sortOrder === "asc"
          ? (parseFloat(a.amount) || 0) - (parseFloat(b.amount) || 0)
          : (parseFloat(b.amount) || 0) - (parseFloat(a.amount) || 0);
      } else if (sortField === "date") {
        aVal = a.date || a.created_at?.split("T")[0] || "";
        bVal = b.date || b.created_at?.split("T")[0] || "";
      } else if (sortField === "status") {
        aVal = a.status || "";
        bVal = b.status || "";
      } else {
        aVal = a[sortField] || "";
        bVal = b[sortField] || "";
      }

      return sortOrder === "asc"
        ? String(aVal).localeCompare(String(bVal), isRTL ? "ar" : "en")
        : String(bVal).localeCompare(String(aVal), isRTL ? "ar" : "en");
    });

    return result;
  }, [
    allReports,
    searchTerm,
    deptFilter,
    statusFilter,
    sortField,
    sortOrder,
    isRTL,
  ]);

  // Dynamic height-based PDF chunking
  const updatePdfChunks = () => {
    if (!filteredAndSortedReports || filteredAndSortedReports.length === 0) {
      setPdfChunks([]);
      return;
    }

    const container = document.createElement("div");
    container.style.cssText =
      "position:absolute;top:-99999px;left:-99999px;width:794px;visibility:hidden;";
    document.body.appendChild(container);

    const testPage = document.createElement("div");
    testPage.style.cssText =
      "width:794px;height:1123px;padding:40px;box-sizing:border-box;border:5px solid #000;";
    const pageBorder = document.createElement("div");
    pageBorder.style.cssText =
      "border:2px solid #000;height:1033px;padding:18px 18px 10px;display:flex;flex-direction:column;box-sizing:border-box;";
    testPage.appendChild(pageBorder);
    container.appendChild(testPage);

    let topHeaderHeight = 220;
    const topHeader = document.getElementById("pdf-pnlt-top-header");
    if (topHeader) {
      const clone = topHeader.cloneNode(true);
      pageBorder.appendChild(clone);
      topHeaderHeight = clone.offsetHeight;
      pageBorder.removeChild(clone);
    }

    let tableHeaderHeight = 35;
    const theadEl = document.getElementById("pdf-pnlt-thead");
    if (theadEl) {
      const tw = document.createElement("table");
      tw.style.cssText = "width:100%;border-collapse:collapse;";
      tw.appendChild(theadEl.cloneNode(true));
      pageBorder.appendChild(tw);
      tableHeaderHeight = tw.offsetHeight;
      pageBorder.removeChild(tw);
    }

    let footerHeight = 70;
    const footerEl = document.getElementById("pdf-pnlt-footer");
    if (footerEl) {
      const clone = footerEl.cloneNode(true);
      pageBorder.appendChild(clone);
      footerHeight = clone.offsetHeight;
      pageBorder.removeChild(clone);
    }

    document.body.removeChild(container);

    const PAGE_MAX_HEIGHT = 1033;
    const availableHeight =
      PAGE_MAX_HEIGHT - topHeaderHeight - footerHeight - 15;

    const tbody = document.getElementById("pdf-pnlt-tbody");
    let rowHeights = [];
    if (tbody) {
      rowHeights = Array.from(tbody.querySelectorAll("tr")).map(
        row => row.offsetHeight || 30,
      );
    } else {
      rowHeights = filteredAndSortedReports.map(() => 30);
    }

    const chunks = [];
    let currentChunk = [];
    let currentHeight = tableHeaderHeight;

    for (let i = 0; i < filteredAndSortedReports.length; i++) {
      const rh = rowHeights[i] || 30;
      if (currentHeight + rh > availableHeight) {
        chunks.push(currentChunk);
        currentChunk = [filteredAndSortedReports[i]];
        currentHeight = tableHeaderHeight + rh;
      } else {
        currentChunk.push(filteredAndSortedReports[i]);
        currentHeight += rh;
      }
    }
    if (currentChunk.length > 0) chunks.push(currentChunk);
    setPdfChunks(chunks);
  };

  useEffect(() => {
    if (filteredAndSortedReports && filteredAndSortedReports.length > 0) {
      const timer = setTimeout(updatePdfChunks, 300);
      return () => clearTimeout(timer);
    } else {
      setPdfChunks([]);
    }
  }, [filteredAndSortedReports]);

  // Pagination
  const totalPages = Math.max(
    1,
    Math.ceil(filteredAndSortedReports.length / perPage),
  );
  const currentPageSafe = Math.min(currentPage, totalPages);
  const paginatedReports = filteredAndSortedReports.slice(
    (currentPageSafe - 1) * perPage,
    currentPageSafe * perPage,
  );

  const handleExportToExcel = () => {
    if (filteredAndSortedReports.length === 0) {
      toast.error(
        t("reports.workingHours.noDataToExport", {
          defaultValue: "No data to export",
        }),
        { rtl: isRTL },
      );
      return;
    }
    const data = filteredAndSortedReports.map((item, index) => ({
      [t("reports.workingHours.excel.serial", {
        defaultValue: isRTL ? "م" : "Serial",
      })]: index + 1,
      [t("reports.workingHours.excel.employee", {
        defaultValue: isRTL ? "الموظف" : "Employee",
      })]: item.employee_name || item.employee?.name || "-",
      [t("employeePenaltiesNamespace.department", {
        defaultValue: isRTL ? "القسم" : "Department",
      })]: item.department || "-",
      [t("employeePenaltiesNamespace.penaltyType", {
        defaultValue: isRTL ? "نوع الجزاء" : "Penalty Type",
      })]: item.penalty_type?.name || item.penalty_type || "-",
      [t("employeePenaltiesNamespace.reason", {
        defaultValue: isRTL ? "السبب" : "Reason",
      })]: item.reason || "-",
      [t("employeePenaltiesNamespace.amount", {
        defaultValue: isRTL ? "المبلغ" : "Amount",
      })]: item.amount || 0,
      [t("employeePenaltiesNamespace.status", {
        defaultValue: isRTL ? "الحالة" : "Status",
      })]: t(`employeePenaltiesNamespace.${item.status}`, {
        defaultValue: item.status,
      }),
      [t("employeePenaltiesNamespace.date", {
        defaultValue: isRTL ? "التاريخ" : "Date",
      })]: item.date || item.created_at?.split("T")[0] || "-",
      [t("employeePenaltiesNamespace.description", {
        defaultValue: isRTL ? "الوصف" : "Description",
      })]: item.description || "-",
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      ws,
      isRTL ? "تقرير الجزاءات" : "Penalties_Report",
    );
    const fileName = `Penalties_Report_${reportDate || new Date().toISOString().split("T")[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success(
      t("reports.workingHours.exportSuccess", {
        defaultValue: "Export success",
      }),
      { rtl: isRTL },
    );
  };

  const handlePrintPdf = async () => {
    if (!filteredAndSortedReports || filteredAndSortedReports.length === 0) {
      toast.error(
        t("reports.workingHours.print.noData", {
          defaultValue: "No data to print",
        }),
        { rtl: isRTL },
      );
      return;
    }
    try {
      setIsGeneratingPdf(true);
      const pdf = new jsPDF("p", "mm", "a4");
      for (let i = 0; i < pdfChunks.length; i++) {
        const element = document.getElementById(
          `penalties-report-pdf-chunk-${i}`,
        );
        if (!element) continue;
        const canvas = await html2canvas(element, {
          scale: 3,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
          onclone: clonedDoc => {
            const el = clonedDoc.getElementById(
              `penalties-report-pdf-chunk-${i}`,
            );
            if (el) {
              el.style.fontFamily =
                '"Times New Roman Bold", "Times New Roman", "Times", serif';
              el.style.fontWeight = "bold";
              el.style.direction = isRTL ? "rtl" : "ltr";
            }
          },
        });
        const imgData = canvas.toDataURL("image/png", 1.0);
        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, 0, 210, 297);
      }
      pdf.save(`ACE_Penalties_Report_${reportDate}.pdf`);
      toast.success(
        t("reports.workingHours.print.success", {
          defaultValue: "PDF Generated Successfully",
        }),
      );
    } catch (err) {
      console.error("PDF generation error:", err);
      toast.error(
        t("reports.workingHours.print.error", {
          defaultValue: "Error generating PDF",
        }),
      );
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  // Shared PDF page content renderer
  const renderPdfPage = (chunk, chunkIdx, allChunks, forPreview = false) => (
    <div
      key={chunkIdx}
      id={!forPreview ? `penalties-report-pdf-chunk-${chunkIdx}` : undefined}
      style={{
        width: "794px",
        height: "1123px",
        backgroundColor: "#ffffff",
        color: "#000000",
        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
        fontWeight: "bold",
        padding: "40px",
        boxSizing: "border-box",
        direction: dir,
        ...(forPreview
          ? {
              marginBottom: chunkIdx < allChunks.length - 1 ? "20px" : "0",
              margin: "0 auto",
            }
          : {}),
      }}
    >
      <div
        style={{
          border: "5px solid #000",
          padding: "3px",
          backgroundColor: "#ffffff",
          boxSizing: "border-box",
          width: "100%",
          height: "100%",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            border: "2px solid #000",
            height: "100%",
            padding: "18px 18px 10px",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            fontFamily:
              '"Times New Roman Bold", "Times New Roman", "Times", serif',
            fontWeight: "bold",
            direction: dir,
            backgroundColor: "#ffffff",
            boxSizing: "border-box",
            width: "100%",
            overflow: "hidden",
          }}
        >
          {/* Watermark */}
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "100%",
              height: "600px",
              backgroundImage: `url(${headerLogo})`,
              backgroundSize: "95%",
              backgroundPosition: "center center",
              backgroundRepeat: "no-repeat",
              opacity: 0.1,
              zIndex: 0,
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            {/* Header Image */}
            <img
              src={`${publicUrl}/HeaderAce.png`}
              alt="Header"
              style={{
                width: "100%",
                height: "auto",
                objectFit: "contain",
                marginBottom: "8px",
                borderBottom: "4px double #000",
                paddingBottom: "8px",
                flexShrink: 0,
              }}
              crossOrigin="anonymous"
            />
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                minHeight: 0,
              }}
            >
              {/* Report Title */}
              <div
                style={{
                  textAlign: "center",
                  marginBottom: "12px",
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: "700",
                    color: "#0d2a50",
                    direction: dir,
                  }}
                >
                  {t("employeePenaltiesNamespace.title", {
                    defaultValue: isRTL ? "تقرير الجزاءات" : "Penalties Report",
                  })}
                </div>
                <div
                  style={{ fontSize: "14px", marginTop: "4px", direction: dir }}
                >
                  {reportDate
                    ? new Date(reportDate + "-01").toLocaleDateString(locale, {
                        year: "numeric",
                        month: "long",
                      })
                    : ""}
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#333",
                    marginTop: "4px",
                    display: "flex",
                    justifyContent: "center",
                    gap: "15px",
                    direction: dir,
                  }}
                >
                  {searchTerm.trim() && (
                    <span>
                      {t("common.search", { defaultValue: "بحث" })}:{" "}
                      {searchTerm}
                    </span>
                  )}
                  {deptFilter !== "all" && (
                    <span>
                      {t("employeePenaltiesNamespace.department")}: {deptFilter}
                    </span>
                  )}
                  {statusFilter !== "all" && (
                    <span>
                      {t("employeePenaltiesNamespace.status")}: {statusFilter}
                    </span>
                  )}
                </div>
                <div
                  style={{
                    fontSize: "13px",
                    color: "#555",
                    marginTop: "2px",
                    direction: dir,
                  }}
                >
                  {t("reports.workingHours.print.employeesCount", {
                    defaultValue: "Count",
                  })}
                  : {filteredAndSortedReports.length}
                </div>
              </div>
              {/* Table */}
              <div style={{ flexShrink: 0 }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "9px",
                    direction: dir,
                    tableLayout: "fixed",
                  }}
                >
                  <colgroup>
                    <col style={{ width: "5%" }} />
                    <col style={{ width: "18%" }} />
                    <col style={{ width: "12%" }} />
                    <col style={{ width: "13%" }} />
                    <col style={{ width: "15%" }} />
                    <col style={{ width: "8%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "9%" }} />
                  </colgroup>
                  <thead>
                    <tr
                      style={{ backgroundColor: "#e3f2fd", color: "#0d2a50" }}
                    >
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "4px 2px",
                          textAlign: "center",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {isRTL ? "م" : "#"}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "4px 2px",
                          textAlign: isRTL ? "right" : "left",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t("reports.workingHours.table.employee")}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "4px 2px",
                          textAlign: "center",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t("employeePenaltiesNamespace.department")}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "4px 2px",
                          textAlign: "center",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t("employeePenaltiesNamespace.penaltyType")}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "4px 2px",
                          textAlign: "center",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t("employeePenaltiesNamespace.reason")}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "4px 2px",
                          textAlign: "center",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t("employeePenaltiesNamespace.amount")}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "4px 2px",
                          textAlign: "center",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t("employeePenaltiesNamespace.status")}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "4px 2px",
                          textAlign: "center",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t("employeePenaltiesNamespace.date")}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "4px 2px",
                          textAlign: "center",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t("employeePenaltiesNamespace.description")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {chunk.map((item, index) => {
                      const cell = {
                        border: "1px solid #000",
                        padding: "4px 2px",
                        fontSize: "8px",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                      };
                      return (
                        <tr key={item.id || index}>
                          <td style={{ ...cell, textAlign: "center" }}>
                            {filteredAndSortedReports.indexOf(item) + 1}
                          </td>
                          <td
                            style={{
                              ...cell,
                              direction: dir,
                              textAlign: isRTL ? "right" : "left",
                            }}
                          >
                            {item.employee_name ||
                              (item.employee
                                ? (isRTL
                                    ? item.employee.name_ar
                                    : item.employee.name_en) ||
                                  item.employee.name
                                : "—")}
                          </td>
                          <td style={{ ...cell, textAlign: "center" }}>
                            {item.department || "-"}
                          </td>
                          <td style={{ ...cell, textAlign: "center" }}>
                            {item.penalty_type?.name ||
                              item.penalty_type ||
                              "-"}
                          </td>
                          <td style={{ ...cell, textAlign: "center" }}>
                            {item.reason || "-"}
                          </td>
                          <td
                            style={{
                              ...cell,
                              textAlign: "center",
                              fontWeight: "700",
                              color: "#AC2000",
                            }}
                          >
                            {item.amount || 0}
                          </td>
                          <td style={{ ...cell, textAlign: "center" }}>
                            {t(`employeePenaltiesNamespace.${item.status}`, {
                              defaultValue: item.status,
                            })}
                          </td>
                          <td style={{ ...cell, textAlign: "center" }}>
                            {item.date || item.created_at?.split("T")[0] || "-"}
                          </td>
                          <td
                            style={{
                              ...cell,
                              textAlign: "center",
                              fontSize: "7px",
                              color: "#555",
                            }}
                          >
                            {item.description || "-"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div
                style={{
                  flex: 1,
                  borderLeft: "1px solid #000",
                  borderRight: "1px solid #000",
                  borderBottom: "1px solid #000",
                  backgroundColor: "rgba(255,255,255,0.7)",
                }}
              />
            </div>
            {/* Footer */}
            <div style={{ flexShrink: 0, paddingTop: "6px" }}>
              <img
                src={`${publicUrl}/FooterAce.png`}
                alt="Footer"
                style={{ width: "100%", height: "auto", objectFit: "contain",
              borderTop: "4px double #000",
              paddingTop: "8px",
}}
                crossOrigin="anonymous"
              />
              <div
                style={{
                  textAlign: "center",
                  marginTop: "4px",
                  fontFamily: '"Times New Roman", "Times", serif',
                }}
              >
                <div
                  style={{
                    fontStyle: "italic",
                    display: "inline-flex",
                    alignItems: "baseline",
                    gap: "6px",
                    fontSize: "12px",
                    fontWeight: "bold",
                    direction: "ltr",
                  }}
                >
                  <span>Page</span>
                  <strong style={{ fontSize: "13px" }}>
                    {chunkIdx + 1} / {allChunks.length}
                  </strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Shared button style
  const btnStyle = (bg, disabled = false) => ({
    backgroundColor: disabled ? "#666" : bg,
    color: "white",
    border: "none",
    padding: "12px 24px",
    borderRadius: "8px",
    cursor: disabled ? "not-allowed" : "pointer",
    fontSize: "14px",
    fontWeight: "bold",
    opacity: disabled ? 0.6 : 1,
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexDirection: isRTL ? "row" : "row-reverse",
  });

  return (
    <div
      style={{
        padding: "30px",
        backgroundColor: "#14263b",
        minHeight: "calc(100vh - 80px)",
        color: "white",
        direction: dir,
        textAlign,
      }}
    >
      {/* Title */}
      <div style={{ marginBottom: "30px" }}>
        <h1
          style={{
            fontSize: "24px",
            fontWeight: "bold",
            color: "white",
            marginBottom: "20px",
            textAlign,
          }}
        >
          {t("employeePenaltiesNamespace.title", {
            defaultValue: isRTL ? "تقرير الجزاءات" : "Penalties Report",
          })}
        </h1>
      </div>

      {/* Top Controls Card */}
      <div
        style={{
          backgroundColor: "#1b3350",
          borderRadius: "12px",
          padding: "20px",
          border: "1px solid #333",
          marginBottom: "30px",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "15px",
            alignItems: "flex-end",
            flexWrap: "wrap",
          }}
        >
          {/* Month Picker */}
          <div style={{ flex: "1", minWidth: "200px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                color: "white",
                fontSize: "14px",
                fontWeight: "500",
                textAlign,
                direction: dir,
              }}
            >
              {t("reports.workingHours.monthYear", {
                defaultValue: "Month/Year",
              })}
            </label>
            <input
              type="month"
              value={reportDate}
              onChange={e => {
                setReportDate(e.target.value);
                setAllReports([]);
                setHasGenerated(false);
                setIsFetchingReport(false);
                dispatch(clearReports());
              }}
              className="month-input-white"
              style={{
                width: "100%",
                padding: "12px 15px",
                backgroundColor: "#1a1f2e",
                border: "1px solid #333",
                borderRadius: "8px",
                color: "white",
                fontSize: "14px",
                direction: dir,
                textAlign,
              }}
            />
          </div>
          {/* Generate Button */}
          <button
            onClick={handleGenerateReport}
            disabled={isLoading || isFetchingReport}
            style={{
              ...btnStyle("#AC2000", isLoading || isFetchingReport),
              flexDirection: "row",
            }}
          >
            <FaCalendarAlt />
            {isLoading || isFetchingReport
              ? t("reports.workingHours.generating", {
                  defaultValue: "Generating...",
                })
              : t("reports.workingHours.showReport", {
                  defaultValue: "Show Report",
                })}
          </button>

          {hasGenerated &&
            !isFetchingReport &&
            filteredAndSortedReports.length > 0 && (
              <>
                {/* Per Page Control */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    flexWrap: "wrap",
                  }}
                >
                  <label
                    style={{
                      color: "white",
                      fontSize: "14px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t("common.perPage", { defaultValue: "عدد السجلات" })}:
                  </label>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    {[
                      {
                        label: "−",
                        fn: () =>
                          setPerPageInput(Math.max(1, perPageInput - 1)),
                        r: "6px 0 0 6px",
                      },
                      null,
                      {
                        label: "+",
                        fn: () =>
                          setPerPageInput(
                            Math.min(
                              filteredAndSortedReports.length || 1000,
                              perPageInput + 1,
                            ),
                          ),
                        r: "0 6px 6px 0",
                      },
                    ].map((btn, i) =>
                      btn ? (
                        <button
                          key={i}
                          onClick={btn.fn}
                          style={{
                            backgroundColor: "#1b3350",
                            color: "white",
                            border: "1px solid #333",
                            padding: "6px 10px",
                            borderRadius: btn.r,
                            cursor: "pointer",
                            fontSize: "14px",
                            fontWeight: "bold",
                            minWidth: "32px",
                          }}
                        >
                          {btn.label}
                        </button>
                      ) : (
                        <input
                          key="input"
                          type="number"
                          min="1"
                          max={filteredAndSortedReports.length || 1000}
                          value={perPageInput}
                          onChange={e =>
                            setPerPageInput(
                              Math.max(1, parseInt(e.target.value) || 1),
                            )
                          }
                          style={{
                            width: "70px",
                            padding: "6px 10px",
                            backgroundColor: "#1a1f2e",
                            border: "1px solid #333",
                            borderLeft: "none",
                            borderRight: "none",
                            borderRadius: 0,
                            color: "white",
                            fontSize: "14px",
                            textAlign: "center",
                          }}
                        />
                      ),
                    )}
                  </div>
                  <button
                    onClick={handleApplyPerPage}
                    style={{
                      backgroundColor: "#AC2000",
                      color: "white",
                      border: "none",
                      padding: "6px 12px",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "13px",
                      fontWeight: "bold",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t("common.apply", { defaultValue: "تطبيق" })}
                  </button>
                </div>
                {/* Preview */}
                <button
                  onClick={() => setIsPreviewOpen(true)}
                  style={btnStyle("#444")}
                >
                  <FaEye />
                  {t("reports.workingHours.preview", {
                    defaultValue: "Preview",
                  })}
                </button>
                {/* Print PDF */}
                <button
                  onClick={handlePrintPdf}
                  disabled={isGeneratingPdf}
                  style={btnStyle("#17a2b8", isGeneratingPdf)}
                >
                  <FaPrint />
                  {isGeneratingPdf
                    ? t("reports.workingHours.print.generating")
                    : t("common.print", {
                        defaultValue: isRTL ? "طباعة PDF" : "Print PDF",
                      })}
                </button>
                {/* Export Excel */}
                <button
                  onClick={handleExportToExcel}
                  style={btnStyle("#3b82f6")}
                >
                  <FaFileExcel />
                  {t("reports.workingHours.exportExcel", {
                    defaultValue: "Export Excel",
                  })}
                </button>
              </>
            )}
        </div>
      </div>

      {/* Results Table Card */}
      {hasGenerated && !isFetchingReport && allReports.length > 0 && (
        <div
          style={{
            backgroundColor: "#1b3350",
            borderRadius: "12px",
            padding: "20px",
            border: "1px solid #333",
          }}
        >
          {/* Search / Filter / Sort Controls */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "15px",
              marginBottom: "20px",
              alignItems: "flex-end",
              borderBottom: "1px solid #333",
              paddingBottom: "20px",
            }}
          >
            {/* Search */}
            <div
              style={{
                flex: "1",
                minWidth: "200px",
                display: "flex",
                flexDirection: "column",
                gap: "6px",
              }}
            >
              <label
                style={{ color: "white", fontSize: "13px", direction: dir }}
              >
                {t("common.search", { defaultValue: "بحث" })}
              </label>
              <input
                type="text"
                placeholder={isRTL ? "بحث عن موظف..." : "Search employee..."}
                value={searchTerm}
                onChange={e => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  padding: "12px 15px",
                  backgroundColor: "#1a1f2e",
                  border: "1px solid #333",
                  borderRadius: "8px",
                  color: "white",
                  fontSize: "14px",
                  direction: dir,
                  textAlign,
                }}
              />
            </div>
            {/* Department Filter */}
            <div
              style={{
                minWidth: "180px",
                display: "flex",
                flexDirection: "column",
                gap: "6px",
              }}
            >
              <label
                style={{ color: "white", fontSize: "13px", direction: dir }}
              >
                {t("employeePenaltiesNamespace.department", {
                  defaultValue: "Department",
                })}
              </label>
              <select
                value={deptFilter}
                onChange={e => {
                  setDeptFilter(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  padding: "12px 15px",
                  backgroundColor: "#1a1f2e",
                  border: "1px solid #333",
                  borderRadius: "8px",
                  color: "white",
                  fontSize: "14px",
                  direction: dir,
                }}
              >
                <option value="all">
                  {t("common.all", { defaultValue: "All" })}
                </option>
                {uniqueDepartments.map(dept => (
                  <option key={dept} value={dept}>
                    {dept}
                  </option>
                ))}
              </select>
            </div>
            {/* Status Filter */}
            <div
              style={{
                minWidth: "180px",
                display: "flex",
                flexDirection: "column",
                gap: "6px",
              }}
            >
              <label
                style={{ color: "white", fontSize: "13px", direction: dir }}
              >
                {t("employeePenaltiesNamespace.status", {
                  defaultValue: "Status",
                })}
              </label>
              <select
                value={statusFilter}
                onChange={e => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  padding: "12px 15px",
                  backgroundColor: "#1a1f2e",
                  border: "1px solid #333",
                  borderRadius: "8px",
                  color: "white",
                  fontSize: "14px",
                  direction: dir,
                }}
              >
                <option value="all">
                  {t("common.all", { defaultValue: "All" })}
                </option>
                <option value="active">
                  {t("employeePenaltiesNamespace.active", {
                    defaultValue: "Active",
                  })}
                </option>
                <option value="approved">
                  {t("employeePenaltiesNamespace.approved", {
                    defaultValue: "Approved",
                  })}
                </option>
                <option value="pending">
                  {t("employeePenaltiesNamespace.pending", {
                    defaultValue: "Pending",
                  })}
                </option>
                <option value="rejected">
                  {t("employeePenaltiesNamespace.rejected", {
                    defaultValue: "Rejected",
                  })}
                </option>
              </select>
            </div>
            {/* Sort Field */}
            <div
              style={{
                minWidth: "180px",
                display: "flex",
                flexDirection: "column",
                gap: "6px",
              }}
            >
              <label
                style={{ color: "white", fontSize: "13px", direction: dir }}
              >
                {t("common.sortBy", { defaultValue: "Sort by" })}
              </label>
              <select
                value={sortField}
                onChange={e => setSortField(e.target.value)}
                style={{
                  padding: "12px 15px",
                  backgroundColor: "#1a1f2e",
                  border: "1px solid #333",
                  borderRadius: "8px",
                  color: "white",
                  fontSize: "14px",
                  direction: dir,
                }}
              >
                <option value="employee_name">
                  {t("reports.workingHours.table.employee")}
                </option>
                <option value="department">
                  {t("employeePenaltiesNamespace.department")}
                </option>
                <option value="amount">
                  {t("employeePenaltiesNamespace.amount")}
                </option>
                <option value="status">
                  {t("employeePenaltiesNamespace.status")}
                </option>
                <option value="date">
                  {t("employeePenaltiesNamespace.date")}
                </option>
              </select>
            </div>
            {/* Sort Order */}
            <SortOrderSelect
              value={sortOrder}
              onChange={setSortOrder}
              style={{ minWidth: "150px" }}
              selectStyle={{ padding: "12px 15px", height: "45px" }}
            />
            {/* Reset */}
            <button
              onClick={() => {
                setSearchTerm("");
                setDeptFilter("all");
                setStatusFilter("all");
                setSortField("employee_name");
                setSortOrder("asc");
                setCurrentPage(1);
              }}
              style={{
                backgroundColor: "#dc3545",
                color: "white",
                border: "none",
                padding: "12px 20px",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "bold",
                height: "45px",
                whiteSpace: "nowrap",
              }}
            >
              {t("common.resetFilters", {
                defaultValue: isRTL ? "مسح الفلاتر" : "Clear Filters",
              })}
            </button>
          </div>

          {/* Table */}
          <div style={{ overflow: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                direction: dir,
              }}
            >
              <thead>
                <tr style={{ borderBottom: "2px solid #AC2000" }}>
                  {[
                    {
                      key: "#",
                      label: t("reports.workingHours.table.number", {
                        defaultValue: "#",
                      }),
                      w: "60px",
                    },
                    {
                      key: "emp",
                      label: t("reports.workingHours.table.employee"),
                      w: "150px",
                    },
                    {
                      key: "dept",
                      label: t("employeePenaltiesNamespace.department"),
                      w: "140px",
                    },
                    {
                      key: "pt",
                      label: t("employeePenaltiesNamespace.penaltyType"),
                      w: "140px",
                    },
                    {
                      key: "reason",
                      label: t("employeePenaltiesNamespace.reason"),
                      w: "160px",
                    },
                    {
                      key: "amt",
                      label: t("employeePenaltiesNamespace.amount"),
                      w: "120px",
                    },
                    {
                      key: "status",
                      label: t("employeePenaltiesNamespace.status"),
                      w: "120px",
                    },
                    {
                      key: "date",
                      label: t("employeePenaltiesNamespace.date"),
                      w: "120px",
                    },
                    {
                      key: "desc",
                      label: t("employeePenaltiesNamespace.description"),
                      w: "180px",
                    },
                  ].map(col => (
                    <th
                      key={col.key}
                      style={{
                        padding: "16px 12px",
                        textAlign: "center",
                        verticalAlign: "middle",
                        color: "white",
                        fontWeight: "bold",
                        minWidth: col.w,
                      }}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedReports.map((item, index) => (
                  <tr
                    key={item.id || index}
                    style={{ borderBottom: "1px solid #333" }}
                  >
                    <td
                      style={{
                        padding: "14px 12px",
                        textAlign: "center",
                        verticalAlign: "middle",
                        color: "white",
                      }}
                    >
                      {filteredAndSortedReports.indexOf(item) + 1}
                    </td>
                    <td
                      style={{
                        padding: "14px 12px",
                        textAlign: "center",
                        verticalAlign: "middle",
                        color: "white",
                        direction: dir,
                      }}
                    >
                      {item.employee_name ||
                        (item.employee
                          ? (isRTL
                              ? item.employee.name_ar
                              : item.employee.name_en) || item.employee.name
                          : "-")}
                    </td>
                    <td
                      style={{
                        padding: "14px 12px",
                        textAlign: "center",
                        verticalAlign: "middle",
                        color: "white",
                        direction: dir,
                      }}
                    >
                      {item.department || "-"}
                    </td>
                    <td
                      style={{
                        padding: "14px 12px",
                        textAlign: "center",
                        verticalAlign: "middle",
                        color: "white",
                        direction: dir,
                      }}
                    >
                      {item.penalty_type?.name || item.penalty_type || "-"}
                    </td>
                    <td
                      style={{
                        padding: "14px 12px",
                        textAlign: "center",
                        verticalAlign: "middle",
                        color: "#ccc",
                        direction: dir,
                      }}
                    >
                      {item.reason || "-"}
                    </td>
                    <td
                      style={{
                        padding: "14px 12px",
                        textAlign: "center",
                        verticalAlign: "middle",
                        color: "#AC2000",
                        fontWeight: "bold",
                        direction: dir,
                      }}
                    >
                      {item.amount || 0}
                    </td>
                    <td
                      style={{
                        padding: "14px 12px",
                        textAlign: "center",
                        verticalAlign: "middle",
                        color: "white",
                        direction: dir,
                      }}
                    >
                      {t(`employeePenaltiesNamespace.${item.status}`, {
                        defaultValue: item.status,
                      })}
                    </td>
                    <td
                      style={{
                        padding: "14px 12px",
                        textAlign: "center",
                        verticalAlign: "middle",
                        color: "white",
                        direction: dir,
                      }}
                    >
                      {item.date || item.created_at?.split("T")[0] || "-"}
                    </td>
                    <td
                      style={{
                        padding: "14px 12px",
                        textAlign: "center",
                        verticalAlign: "middle",
                        color: "#999",
                        fontSize: "13px",
                        direction: dir,
                      }}
                    >
                      {item.description || "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "8px",
                marginTop: "20px",
                paddingBottom: "20px",
                direction: dir,
              }}
            >
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPageSafe - 1))}
                disabled={currentPageSafe === 1}
                style={{
                  padding: "8px 12px",
                  backgroundColor: currentPageSafe === 1 ? "#333" : "#AC2000",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: currentPageSafe === 1 ? "not-allowed" : "pointer",
                }}
              >
                {t("common.previous", { defaultValue: "Previous" })}
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(
                  p =>
                    p === 1 ||
                    p === totalPages ||
                    (p >= currentPageSafe - 2 && p <= currentPageSafe + 2),
                )
                .map((page, idx, arr) => {
                  const showEllipsis = idx > 0 && page - arr[idx - 1] > 1;
                  return (
                    <React.Fragment key={page}>
                      {showEllipsis && (
                        <span style={{ padding: "8px", color: "white" }}>
                          ...
                        </span>
                      )}
                      <button
                        onClick={() => setCurrentPage(page)}
                        style={{
                          padding: "8px 12px",
                          backgroundColor:
                            page === currentPageSafe ? "#AC2000" : "#1a1f2e",
                          color: "white",
                          border: "1px solid #333",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  );
                })}
              <button
                onClick={() =>
                  setCurrentPage(Math.min(totalPages, currentPageSafe + 1))
                }
                disabled={currentPageSafe === totalPages}
                style={{
                  padding: "8px 12px",
                  backgroundColor:
                    currentPageSafe === totalPages ? "#333" : "#AC2000",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor:
                    currentPageSafe === totalPages ? "not-allowed" : "pointer",
                }}
              >
                {t("common.next", { defaultValue: "Next" })}
              </button>
            </div>
          )}
        </div>
      )}

      {/* No Data */}
      {hasGenerated &&
        !isFetchingReport &&
        penaltiesReports &&
        allReports.length === 0 && (
          <div
            style={{
              backgroundColor: "#1b3350",
              borderRadius: "12px",
              padding: "40px 20px",
              border: "1px solid #333",
              textAlign: "center",
            }}
          >
            <div style={{ color: "#999", fontSize: "18px", fontWeight: "500" }}>
              {t("reports.workingHours.noData", {
                defaultValue: "No data found",
              })}
            </div>
          </div>
        )}

      {/* Measurement Container (offscreen) */}
      {hasGenerated &&
        !isFetchingReport &&
        filteredAndSortedReports.length > 0 && (
          <div
            style={{
              position: "absolute",
              top: "-99999px",
              left: "-99999px",
              width: "794px",
            }}
          >
            <div
              style={{
                width: "794px",
                backgroundColor: "#ffffff",
                color: "#000",
                fontFamily: '"Times New Roman", serif',
                padding: "40px",
                boxSizing: "border-box",
                direction: dir,
              }}
            >
              <div
                style={{
                  border: "5px solid #000",
                  padding: "3px",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <div
                  style={{
                    border: "2px solid #000",
                    padding: "18px 18px 10px",
                    display: "flex",
                    flexDirection: "column",
                    direction: dir,
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                >
                  <div id="pdf-pnlt-top-header">
                    <img
                      src={`${publicUrl}/HeaderAce.png`}
                      alt="Header"
                      style={{
                        width: "100%",
                        height: "auto",
                        objectFit: "contain",
                        marginBottom: "8px",
                        borderBottom: "4px double #000",
                        paddingBottom: "8px",
                      }}
                      crossOrigin="anonymous"
                    />
                    <div style={{ textAlign: "center", marginBottom: "12px" }}>
                      <div
                        style={{
                          fontSize: "20px",
                          fontWeight: "700",
                          color: "#0d2a50",
                        }}
                      >
                        {t("employeePenaltiesNamespace.title")}
                      </div>
                      <div style={{ fontSize: "14px", marginTop: "4px" }}>
                        {reportDate
                          ? new Date(reportDate + "-01").toLocaleDateString(
                              locale,
                              { year: "numeric", month: "long" },
                            )
                          : ""}
                      </div>
                      <div
                        style={{
                          fontSize: "13px",
                          color: "#555",
                          marginTop: "2px",
                        }}
                      >
                        Count: {filteredAndSortedReports.length}
                      </div>
                    </div>
                  </div>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "9px",
                      tableLayout: "fixed",
                    }}
                  >
                    <colgroup>
                      <col style={{ width: "5%" }} />
                      <col style={{ width: "18%" }} />
                      <col style={{ width: "12%" }} />
                      <col style={{ width: "13%" }} />
                      <col style={{ width: "15%" }} />
                      <col style={{ width: "8%" }} />
                      <col style={{ width: "10%" }} />
                      <col style={{ width: "10%" }} />
                      <col style={{ width: "9%" }} />
                    </colgroup>
                    <thead id="pdf-pnlt-thead">
                      <tr
                        style={{ backgroundColor: "#e3f2fd", color: "#0d2a50" }}
                      >
                        <th
                          style={{
                            border: "1px solid #000",
                            padding: "4px 2px",
                            textAlign: "center",
                          }}
                        >
                          {isRTL ? "م" : "#"}
                        </th>
                        <th
                          style={{
                            border: "1px solid #000",
                            padding: "4px 2px",
                            textAlign: isRTL ? "right" : "left",
                          }}
                        >
                          {t("reports.workingHours.table.employee")}
                        </th>
                        <th
                          style={{
                            border: "1px solid #000",
                            padding: "4px 2px",
                            textAlign: "center",
                          }}
                        >
                          {t("employeePenaltiesNamespace.department")}
                        </th>
                        <th
                          style={{
                            border: "1px solid #000",
                            padding: "4px 2px",
                            textAlign: "center",
                          }}
                        >
                          {t("employeePenaltiesNamespace.penaltyType")}
                        </th>
                        <th
                          style={{
                            border: "1px solid #000",
                            padding: "4px 2px",
                            textAlign: "center",
                          }}
                        >
                          {t("employeePenaltiesNamespace.reason")}
                        </th>
                        <th
                          style={{
                            border: "1px solid #000",
                            padding: "4px 2px",
                            textAlign: "center",
                          }}
                        >
                          {t("employeePenaltiesNamespace.amount")}
                        </th>
                        <th
                          style={{
                            border: "1px solid #000",
                            padding: "4px 2px",
                            textAlign: "center",
                          }}
                        >
                          {t("employeePenaltiesNamespace.status")}
                        </th>
                        <th
                          style={{
                            border: "1px solid #000",
                            padding: "4px 2px",
                            textAlign: "center",
                          }}
                        >
                          {t("employeePenaltiesNamespace.date")}
                        </th>
                        <th
                          style={{
                            border: "1px solid #000",
                            padding: "4px 2px",
                            textAlign: "center",
                          }}
                        >
                          {t("employeePenaltiesNamespace.description")}
                        </th>
                      </tr>
                    </thead>
                    <tbody id="pdf-pnlt-tbody">
                      {filteredAndSortedReports.map((item, index) => {
                        const cell = {
                          border: "1px solid #000",
                          padding: "4px 2px",
                          fontSize: "8px",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        };
                        return (
                          <tr key={item.id || index}>
                            <td style={{ ...cell, textAlign: "center" }}>
                              {index + 1}
                            </td>
                            <td
                              style={{
                                ...cell,
                                textAlign: isRTL ? "right" : "left",
                              }}
                            >
                              {item.employee_name ||
                                (item.employee
                                  ? (isRTL
                                      ? item.employee.name_ar
                                      : item.employee.name_en) ||
                                    item.employee.name
                                  : "—")}
                            </td>
                            <td style={{ ...cell, textAlign: "center" }}>
                              {item.department || "-"}
                            </td>
                            <td style={{ ...cell, textAlign: "center" }}>
                              {item.penalty_type?.name ||
                                item.penalty_type ||
                                "-"}
                            </td>
                            <td style={{ ...cell, textAlign: "center" }}>
                              {item.reason || "-"}
                            </td>
                            <td
                              style={{
                                ...cell,
                                textAlign: "center",
                                fontWeight: "700",
                                color: "#AC2000",
                              }}
                            >
                              {item.amount || 0}
                            </td>
                            <td style={{ ...cell, textAlign: "center" }}>
                              {t(`employeePenaltiesNamespace.${item.status}`, {
                                defaultValue: item.status,
                              })}
                            </td>
                            <td style={{ ...cell, textAlign: "center" }}>
                              {item.date ||
                                item.created_at?.split("T")[0] ||
                                "-"}
                            </td>
                            <td
                              style={{
                                ...cell,
                                textAlign: "center",
                                fontSize: "7px",
                              }}
                            >
                              {item.description || "-"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div
                    id="pdf-pnlt-footer"
                    style={{ marginTop: "auto", paddingTop: "6px" }}
                  >
                    <img
                      src={`${publicUrl}/FooterAce.png`}
                      alt="Footer"
                      style={{
                        width: "100%",
                        height: "auto",
                        objectFit: "contain",
              borderTop: "4px double #000",
              paddingTop: "8px",

                      }}
                      crossOrigin="anonymous"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      {/* PDF Print Template (offscreen) */}
      {pdfChunks.length > 0 && (
        <div
          style={{ position: "absolute", top: "-99999px", left: "-99999px" }}
        >
          {pdfChunks.map((chunk, chunkIdx, allChunks) =>
            renderPdfPage(chunk, chunkIdx, allChunks, false),
          )}
        </div>
      )}

      {/* PDF Preview Modal */}
      <PdfPreviewModal
        isOpen={isPreviewOpen && filteredAndSortedReports.length > 0}
        title={t("employeePenaltiesNamespace.title", {
          defaultValue: "Penalties",
        })}
        onClose={() => setIsPreviewOpen(false)}
        onPrint={handlePrintPdf}
        isGeneratingPdf={isGeneratingPdf}
      >
        <div
          style={{
            width: "794px",
            backgroundColor: "#ffffff",
            color: "#000",
            fontFamily: '"Times New Roman", serif',
            boxSizing: "border-box",
            direction: dir,
            transformOrigin: "top center",
          }}
        >
          {pdfChunks.map((chunk, chunkIdx, allChunks) =>
            renderPdfPage(chunk, chunkIdx, allChunks, true),
          )}
        </div>
      </PdfPreviewModal>
    </div>
  );
};

export default PenaltiesReport;
